import { useContext, useEffect, useMemo, useState } from "react";
import { CompanyContext } from "../../../contexts/company-context";
import { BranchOperation } from "../../../serviceoperation/BranchOperation";
import { LocationType } from "../modal/location-types";
import { timeOffStatus } from "../../../constants";
import { ScheduleType } from "../components/week/week-types";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";

export const useSchedule = (
  selectedWeek: string[],
  filterBranch?: React.Key,
  refreshData?: boolean,
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { selectedCompany } = useContext(CompanyContext);

  // Location Data, and ScheduleData for the incoming week!
  const [location, setLocation] = useState<LocationType[]>([]);
  const [selectedWeekSchedule, setSelectedWeekSchedule] = useState<
    ScheduleType[]
  >([]);

  const branchOps = useMemo(() => new BranchOperation(), []);
  const scheduleOps = useMemo(() => new ScheduleOperation(), []);

  useMemo(() => {
    branchOps
      .getBranchAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        if (value.data) {
          setLocation(
            value.data.map((s) => ({
              key: s.branchID,
              name: s.name,
              color: s.color,
            })),
          );
        }
      });
  }, [selectedCompany]);

  const categoryColors: any = {
    scheduled: "green",
    timeoffpending: "orange",
    timeoff: "red",
  };

  useEffect(() => {
    setLoading(true);
    if (!selectedWeek || selectedWeek.length === 0) return;

    scheduleOps
      .getScheduleAsync(selectedCompany?.corporationId as number, {
        weekOf: selectedWeek[0],
        page: 1,
        pageSize: 255,
        ...(filterBranch !== undefined &&
          filterBranch !== 0 && { branchId: filterBranch }),
      })
      .then(({ results }) => {
        const groupedResult = groupBy(results, (p) => p.employee.employeeID);
        const employeeSchedule = Object.keys(groupedResult).map((key) => {
          const root: ScheduleType = {
            key: "",
            name: "",
            initial: "",
            days: Array.from({ length: 7 }, () => []),
          };

          groupedResult[key].forEach((x) => {
            const worktime = new Date(x.workDate);
            worktime.setHours(x.startWorkHour, x.startWorkMinute);
            const workStartTime = worktime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });

            worktime.setHours(x.endWorkHour, x.endWorkMinute);
            const workEndTime = worktime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });

            const workDay = new Date(x.workDate).getDay();

            root.key = x.employee.employeeID;
            root.name = `${x.employee.firstName} ${x.employee.lastName}`;
            root.initial = x.employee.firstName;
            root.days[workDay].push({
              key: x.scheduleID,
              time: `${workStartTime} - ${workEndTime}`,
              color: x.branch?.color || "#1890ff",
              isPublished: !(x.isDraft ?? false),
              secondcolor:
                x.timeoffDetail &&
                x.timeoffDetail.timeOffStatusId === timeOffStatus.APPROVE
                  ? categoryColors["timeoff"]
                  : x.timeoffDetail &&
                    x.timeoffDetail.timeOffStatusId === timeOffStatus.REQUESTED
                  ? categoryColors["timeoffpending"]
                  : categoryColors["scheduled"],
              tag:
                x.timeoffDetail &&
                x.timeoffDetail.timeOffStatusId === timeOffStatus.APPROVE
                  ? "TimeOff"
                  : x.timeoffDetail &&
                    x.timeoffDetail.timeOffStatusId === timeOffStatus.REQUESTED
                  ? "TimeOff Pending"
                  : "Scheduled",
            });
          });

          return root;
        });
        setSelectedWeekSchedule(employeeSchedule);
        setLoading(false);
      });
  }, [selectedWeek, filterBranch, selectedCompany, refreshData]);

  // GroupBY Function...
  function groupBy<T>(arr: T[], fn: (item: T) => any) {
    return arr.reduce<Record<string, T[]>>((prev, curr) => {
      const groupKey = fn(curr);
      const group = prev[groupKey] || [];
      group.push(curr);
      return { ...prev, [groupKey]: group };
    }, {});
  }

  return {
    selectedWeekSchedule,
    location,
    loading,
  };
};
